import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, ArrowLeft, Copy } from "lucide-react";
import { apiPost } from "../lib/api";
import { useAuth } from "../lib/auth";
import Masthead from "../components/Masthead";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

function formatErr(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  return e?.message || "Something went wrong.";
}

export default function Enroll2FA() {
  const { refresh, user } = useAuth();
  const nav = useNavigate();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [backup, setBackup] = useState(null);

  const start = async () => {
    setBusy(true);
    setErr(null);
    try {
      const d = await apiPost("/auth/totp/setup");
      setSetup(d);
    } catch (e) { setErr(formatErr(e)); } finally { setBusy(false); }
  };

  useEffect(() => { if (!user?.totp_enrolled) start(); /* eslint-disable-next-line */ }, []);

  const enable = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const d = await apiPost("/auth/totp/enable", { code });
      setBackup(d.backup_codes);
      await refresh();
    } catch (e) { setErr(formatErr(e)); } finally { setBusy(false); }
  };

  const disable = async () => {
    if (!confirm("Disable 2FA on this account?")) return;
    await apiPost("/auth/totp/disable");
    await refresh();
    setSetup(null);
    setBackup(null);
  };

  return (
    <>
      <Masthead
        eyebrow="Account Security"
        title="Two-Factor Authentication"
        subtitle="Add a time-based one-time code (TOTP) to your sign-in — compatible with Google Authenticator, Authy, 1Password."
        module={null}
        meta={<Button variant="ghost" size="sm" onClick={() => nav("/")} data-testid="enroll-back"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>}
      />
      <div className="px-[26px] py-6 flex-1 max-w-2xl">
        {user?.totp_enrolled && !backup ? (
          <div className="cv-panel" data-testid="already-enrolled">
            <div className="flex items-center gap-2 text-[15px] font-bold" style={{ color: "var(--cv-green)" }}>
              <ShieldCheck className="w-5 h-5" /> 2FA is enabled on your account
            </div>
            <p className="text-[13.5px] mt-2" style={{ color: "var(--cv-charcoal-soft)" }}>
              You'll be prompted for a 6-digit code from your authenticator app every time you sign in.
            </p>
            <Button variant="outline" className="mt-4" data-testid="disable-2fa" onClick={disable}>Disable 2FA</Button>
          </div>
        ) : backup ? (
          <div className="cv-panel" data-testid="backup-codes">
            <div className="flex items-center gap-2 text-[16px] font-bold" style={{ color: "var(--cv-green)" }}>
              <ShieldCheck className="w-5 h-5" /> 2FA enabled — save your backup codes
            </div>
            <p className="text-[13px] mt-2" style={{ color: "var(--cv-charcoal-soft)" }}>
              If you lose access to your authenticator, use one of these codes to sign in. Each code works once.
            </p>
            <div className="grid grid-cols-2 gap-2 mt-3 font-mono text-[14px]">
              {backup.map((c, i) => (
                <div key={c} data-testid={`backup-code-${i}`} className="px-3 py-2 rounded bg-white" style={{ border: "1px dashed var(--cv-line)" }}>{c}</div>
              ))}
            </div>
            <Button className="mt-4" style={{ background: "var(--cv-crimson)" }} onClick={() => nav("/")} data-testid="backup-continue">
              I've saved my codes — continue
            </Button>
          </div>
        ) : setup ? (
          <div className="grid md:grid-cols-2 gap-4">
            <div className="cv-panel" data-testid="enroll-qr">
              <div className="text-[13px] small-caps font-bold" style={{ color: "var(--cv-charcoal-soft)" }}>Step 1 · Scan the QR</div>
              <img alt="TOTP QR" data-testid="qr-image" className="mt-3 rounded"
                style={{ width: 220, height: 220, background: "#fff", padding: 8 }}
                src={`data:image/png;base64,${setup.qr_png_base64}`} />
              <div className="text-[12px] mt-3" style={{ color: "var(--cv-charcoal-soft)" }}>
                Or enter this secret manually:
              </div>
              <div className="flex items-center gap-2 mt-1">
                <code data-testid="totp-secret" className="text-[12px] px-2 py-1 rounded bg-white break-all" style={{ border: "1px solid var(--cv-line)" }}>
                  {setup.secret}
                </code>
                <button type="button" onClick={() => navigator.clipboard?.writeText(setup.secret)}
                  className="p-1.5 rounded" style={{ border: "1px solid var(--cv-line)" }} aria-label="copy">
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <form onSubmit={enable} className="cv-panel" data-testid="enroll-form">
              <div className="text-[13px] small-caps font-bold" style={{ color: "var(--cv-charcoal-soft)" }}>Step 2 · Confirm</div>
              <div className="text-[16px] font-bold mt-1" style={{ color: "var(--cv-crimson)" }}>Enter the 6-digit code</div>
              <p className="text-[13px] mt-1" style={{ color: "var(--cv-charcoal-soft)" }}>
                Open your authenticator app and enter the code it shows for CyberVisible.
              </p>
              <Input data-testid="enroll-code" required inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="123 456" className="mt-3 tracking-[0.4em] text-center text-[22px] font-serif-display" />
              {err && <div data-testid="enroll-error" className="mt-2 text-[13px] px-3 py-2 rounded"
                style={{ background: "rgba(165,48,15,0.13)", color: "var(--cv-crimson)" }}>{err}</div>}
              <Button data-testid="enroll-submit" disabled={busy || code.length !== 6}
                type="submit" className="w-full mt-3" style={{ background: "var(--cv-crimson)" }}>
                {busy ? "Enabling…" : "Enable 2FA"}
              </Button>
            </form>
          </div>
        ) : (
          <div className="opacity-60 text-sm">Loading enrollment…</div>
        )}
      </div>
    </>
  );
}
